// VendAgain — my listings dashboard. Grouped by active vs sold.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, MapPin } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CategoryChip, ConditionPill, formatPrice, CAT_MAP } from "./shared";

export default function VendAgainMine() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/vendagain/listings/mine").then(({ data }) => {
      setItems(data.items || []);
    }).finally(() => setLoading(false));
  }, []);

  const active = items.filter((i) => !i.sold);
  const sold = items.filter((i) => i.sold);

  return (
    <div className="mx-auto max-w-5xl px-6 py-14" data-testid="vendagain-mine-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline" style={{ color: "var(--avt-purple)" }}>VendAgain</div>
          <h1 className="font-serif text-4xl">My listings</h1>
          <p className="mt-1 opacity-70 text-sm">Everything you&rsquo;ve posted — active on top, sold history below.</p>
        </div>
        <Link to="/vendagain/new">
          <Button className="btn-primary rounded-full inline-flex items-center gap-2" data-testid="vendagain-mine-new-btn">
            <Plus size={16} /> Post an item
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="py-16 opacity-60">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-24 text-center border-2 border-dashed rounded-sm"
             style={{ borderColor: "rgba(0,0,0,0.08)" }}
             data-testid="vendagain-mine-empty">
          <Package className="mx-auto h-10 w-10 opacity-30" />
          <h3 className="mt-4 font-serif text-2xl">Nothing posted yet.</h3>
          <p className="mt-2 text-sm opacity-70">Turn leftover gear or gently-used items into cash.</p>
          <Link to="/vendagain/new" className="mt-4 inline-block">
            <Button className="btn-primary rounded-full">Post your first item</Button>
          </Link>
        </div>
      ) : (
        <>
          <ListingRows title="Active" items={active} testKey="active" />
          {sold.length > 0 && <ListingRows title="Sold" items={sold} testKey="sold" />}
        </>
      )}
    </div>
  );
}

function ListingRows({ title, items, testKey }) {
  return (
    <section className="mb-10" data-testid={`vendagain-mine-${testKey}`}>
      <div className="overline mb-3">{title} · {items.length}</div>
      <div className="space-y-3">
        {items.map((i) => {
          const cat = CAT_MAP[i.category];
          const CatIcon = cat?.icon;
          return (
            <Link
              key={i.id}
              to={`/vendagain/listings/${i.id}`}
              className="card-warm rounded-sm p-4 flex items-center gap-4 hover:shadow"
              data-testid={`vendagain-mine-card-${i.id}`}
            >
              <div className="w-20 h-20 rounded-sm overflow-hidden bg-[color:var(--sand)] flex-shrink-0">
                {i.image_urls[0] ? (
                  <img src={i.image_urls[0]} alt="" className="w-full h-full object-cover" />
                ) : CatIcon ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <CatIcon className="h-8 w-8 opacity-40" style={{ color: cat.ink }} />
                  </div>
                ) : null}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <CategoryChip value={i.category} />
                  <ConditionPill value={i.condition} />
                  {typeof i.contact_requests === "number" && i.contact_requests > 0 && (
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                          style={{ background: "rgba(107,47,186,0.14)", color: "var(--avt-purple)" }}>
                      {i.contact_requests} interested
                    </span>
                  )}
                </div>
                <div className="font-bold truncate">{i.title}</div>
                <div className="text-xs opacity-70 inline-flex items-center gap-1 mt-0.5">
                  <MapPin size={11} /> {i.city}
                </div>
              </div>
              <div className="text-lg font-bold flex-shrink-0">
                {formatPrice(i.price_cents, i.best_offer)}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
